import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { legalVaultService } from '../services/legalVaultService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiArrowRight, FiAlertCircle, FiCheckCircle } = FiIcons;

async function reconcileMissingPublishedLinks() {
  if (!legalVaultService.hasSettingsAccess()) return;
  try {
    const client=await legalVaultService.requirePocketBase('reconcileMissingPublishedLinks');
    const [documents,links]=await Promise.all([
      client.collection('legal_documents').getFullList({
        filter:'status="published" && (visibility="public" || visibility="unlisted")',
      }),
      client.collection('legal_public_links').getFullList({}),
    ]);
    const now=new Date().toISOString();
    for (const document of documents) {
      const documentLinks=links.filter((link)=> link.documentId===document.id);
      const usableActiveLinks=documentLinks.filter((link)=> link.isActive===true && (!link.expiresAt || link.expiresAt>now));
      const hasCanonicalActiveLink=usableActiveLinks.some((link)=> link.slug===document.slug);
      if (hasCanonicalActiveLink) continue;
      const hasOnlyDisabledOrExpiredLinks=documentLinks.length>0 && usableActiveLinks.length===0;
      if (hasOnlyDisabledOrExpiredLinks) continue;
      await legalVaultService.ensurePublicLinkForDocument(document.id);
    }
  } catch (error) {
    console.warn('Public link reconciliation skipped:',error);
  }
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('bpaLegalVault_lastEmail');
    if (rememberedEmail) setEmail(rememberedEmail);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const user = await legalVaultService.login(email, password);
      if (user?.active === false) {
        legalVaultService.logout();
        setError('Your account is inactive. Please contact support.');
        return;
      }
      if (!legalVaultService.hasViewerAccess()) {
        legalVaultService.logout();
        setError('You do not have permission to access this system.');
        return;
      }
      await reconcileMissingPublishedLinks();
      localStorage.setItem('bpaLegalVault_lastEmail', email);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // FIXED: B. Harden Password Reset
  const handleResetPassword = async () => {
    if (!email) { setError('Please enter your email address first'); return; }
    setResetLoading(true);
    setError('');
    setSuccess('');
    try {
      await legalVaultService.requestPasswordReset(email);
      setSuccess('Password reset instructions have been sent to your email');
    } catch (err) {
      setError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex-col justify-center items-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 via-transparent to-transparent"></div>
        <div className="w-24 h-24 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-3xl border border-white/10 shadow-2xl mb-8 z-10">LV</div>
        <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-4xl font-bold text-white mb-4 z-10 tracking-tight">BPA Legal Vault</motion.h1>
        <p className="text-slate-300 text-lg text-center max-w-md z-10 leading-relaxed">Secure legal document management with controlled access and version tracking.</p>
        <div className="mt-12 space-y-4 z-10">
          <div className="flex items-center text-slate-300"><SafeIcon icon={FiCheckCircle} className="text-blue-400 mr-3" /><span className="text-sm">Controlled Admin Access</span></div>
          <div className="flex items-center text-slate-300"><SafeIcon icon={FiCheckCircle} className="text-blue-400 mr-3" /><span className="text-sm">Audit Records</span></div>
          <div className="flex items-center text-slate-300"><SafeIcon icon={FiCheckCircle} className="text-blue-400 mr-3" /><span className="text-sm">Branded Document Publishing</span></div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 bg-gradient-to-br from-slate-50 to-slate-100">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-white/50 shadow-xl p-8 sm:p-10">
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">BPA Legal Vault</h1>
              <p className="text-slate-500">Secure Admin Access</p>
            </div>
            {error && <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start text-red-700 text-sm"><SafeIcon icon={FiAlertCircle} className="mr-2 mt-0.5" />{error}</div>}
            {success && <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start text-emerald-700 text-sm"><SafeIcon icon={FiCheckCircle} className="mr-2 mt-0.5" />{success}</div>}
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg text-sm" placeholder="name@bpasolutions.au" required disabled={loading} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg text-sm" placeholder="••••••••" required disabled={loading} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center text-sm text-slate-600"><input type="checkbox" className="mr-2" /> Remember me</label>
                <button type="button" onClick={handleResetPassword} disabled={resetLoading} className="text-sm text-blue-600 font-medium">{resetLoading ? 'Sending...' : 'Reset password'}</button>
              </div>
              <button type="submit" disabled={loading} className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium shadow-lg disabled:opacity-50">
                {loading ? 'Signing In...' : <>Sign In <SafeIcon icon={FiArrowRight} className="ml-2" /></>}
              </button>
            </form>
            <div className="mt-8 pt-6 border-t border-slate-200 text-center">
              <div className={`inline-flex items-center text-xs ${legalVaultService.isConnected() ? 'text-emerald-600' : 'text-slate-500'}`}>
                <div className={`w-2 h-2 rounded-full mr-2 ${legalVaultService.isConnected() ? 'bg-emerald-500' : 'bg-slate-400'}`}></div>
                Secure backend {legalVaultService.isConnected() ? 'connected' : 'unavailable'}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}